import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function NewsSection({ user }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNews, setSelectedNews] = useState(null);
  const [generatingPost, setGeneratingPost] = useState(false);
  const [generatedPost, setGeneratedPost] = useState(null);

  const effectiveIndustry = user.current_industry || user.industry;

  useEffect(() => {
    fetchNews();
  }, [effectiveIndustry]);

  const fetchNews = async () => {
    setLoading(true);
    try {
      // Backend will auto-detect region and apply appropriate filters
      const response = await axios.get(`${API}/news/${effectiveIndustry}`);
      setNews(response.data.articles || []);
    } catch (error) {
      console.error('Error fetching news:', error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (newsItem) => {
    setSelectedNews(newsItem);
    setGeneratingPost(true);
    setGeneratedPost(null);

    try {
      const response = await axios.post(`${API}/news/generate-post`, {
        user_id: user.id,
        news_title: newsItem.title,
        news_description: newsItem.description,
        news_url: newsItem.url,
        industry: effectiveIndustry
      });

      setGeneratedPost(response.data);
    } catch (error) {
      console.error('Error generating post:', error);
      alert('Failed to generate post from news');
    } finally {
      setGeneratingPost(false);
    }
  };

  const saveGeneratedPost = async () => {
    try {
      await axios.post(`${API}/posts`, {
        user_id: user.id,
        platform: 'General',
        industry: effectiveIndustry,
        content: generatedPost.content,
        image_base64: generatedPost.image_base64
      });

      alert('✅ Post saved successfully!');
      setSelectedNews(null);
      setGeneratedPost(null);
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Failed to save post');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white">📰 Industry News</h2>
          <p className="style={{ color: 'var(--text-muted)' }} mt-1">Curated business news for {effectiveIndustry} • Auto-filtered by region</p>
        </div>
        <button
          onClick={fetchNews}
          disabled={loading}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition disabled:opacity-50"
        >
          🔄 Refresh
        </button>
      </div>

      {/* News Grid */}
      {loading ? (
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
          <p className="style={{ color: 'var(--text-muted)' }} mt-4">Loading news...</p>
        </div>
      ) : news.length === 0 ? (
        <div className="text-center py-20 bg-gray-800/50 rounded-xl border border-gray-700">
          <p className="style={{ color: 'var(--text-muted)' }} text-lg">📰 No news available</p>
          <button onClick={fetchNews} className="mt-4 text-orange-500 hover:text-orange-400">
            Try again
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {news.map((item, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl overflow-hidden border border-gray-700 hover:border-orange-500 transition shadow-lg"
            >
              {/* News Image */}
              {item.urlToImage && (
                <div className="h-48 overflow-hidden bg-gray-700">
                  <img
                    src={item.urlToImage}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* News Content */}
              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between text-xs style={{ color: 'var(--text-muted)' }}">
                  <span className="bg-orange-500/20 text-orange-400 px-2 py-1 rounded">
                    {item.source?.name || 'News Source'}
                  </span>
                  <span>{new Date(item.publishedAt).toLocaleDateString()}</span>
                </div>

                <h3 className="text-white font-semibold text-lg line-clamp-3 leading-tight">
                  {item.title}
                </h3>

                <p className="style={{ color: 'var(--text-muted)' }} text-sm line-clamp-2">
                  {item.description}
                </p>

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => handleCreatePost(item)}
                    className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition font-medium text-sm"
                  >
                    ✨ Create Post
                  </button>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition text-sm"
                  >
                    📖 Read
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Generate Post Modal */}
      {selectedNews && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-orange-500/50 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500/20 to-pink-500/20 border-b border-orange-500/30 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-white">✨ Generate Post from News</h3>
                <button
                  onClick={() => {
                    setSelectedNews(null);
                    setGeneratedPost(null);
                  }}
                  className="style={{ color: 'var(--text-muted)' }} hover:text-white transition text-2xl"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Original News */}
              <div className="theme-card-nested border rounded-lg p-4">
                <p className="text-sm style={{ color: 'var(--text-muted)' }} mb-2">Original News:</p>
                <h4 className="text-white font-semibold">{selectedNews.title}</h4>
              </div>

              {/* Generated Post */}
              {generatingPost ? (
                <div className="text-center py-10">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
                  <p className="style={{ color: 'var(--text-muted)' }} mt-4">AI is creating your post...</p>
                </div>
              ) : generatedPost ? (
                <div className="space-y-4">
                  <div className="bg-gray-800/50 border border-green-500/30 rounded-lg p-4">
                    <p className="text-sm text-green-400 mb-2">✅ Generated Content:</p>
                    <p className="text-white whitespace-pre-line">{generatedPost.content}</p>
                  </div>

                  {generatedPost.image_base64 && (
                    <div className="theme-card-nested border rounded-lg p-4">
                      <p className="text-sm style={{ color: 'var(--text-muted)' }} mb-2">Generated Image:</p>
                      <img
                        src={`data:image/png;base64,${generatedPost.image_base64}`}
                        alt="Generated"
                        className="w-full rounded-lg"
                      />
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={saveGeneratedPost}
                      className="flex-1 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition font-medium"
                    >
                      💾 Save to Posts
                    </button>
                    <button
                      onClick={() => handleCreatePost(selectedNews)}
                      className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition"
                    >
                      🔄 Regenerate
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NewsSection;
